<template>
    <QrHeader :text="trans('header.design-your-qr-chip')" trans="header.design-your-qr-chip"/>

    <div class="mt-4">
        <div class="sm:grid grid-cols-12 gap-x-4">
            <div class="col-span-6 order-1">
                <Preview :qr-code="qrCode" :key="qrCode.updated_at"/>
            </div>

            <QrForm class="space-y-4 col-span-6 sm:mt-0 mt-4">
                <div v-if="qrCode.product.allows_image">
                    <QrFilesUpload
                        v-if="qrCode.product.allows_image"
                        :upload-files="uploadFiles"
                        :helper-text="trans('qrcode.helper-text')"
                        direct-upload
                    />

                    <ImageCrop
                        v-if="qrCode.image"
                        :key="qrCode.updated_at"
                        :model="qrCode"
                        :file="qrCode.image.file"
                        class="mt-4"
                    />
                </div>
                <div v-else>
                    <QrParagraph :text="trans('qrcode.text-above')" trans="qrcode.text-above"/>
                    <div class="flex justify-between">
                        <QrInput
                            v-model="qrCode.text_top"
                            @changed-debounce="qrCodeStore.update(null, true)"
                            class="w-full"
                        />

                        <StyleSelection
                            :style="qrCode.text_top_style"
                            @change="style => qrCode.text_top_style = style"
                            class="ml-2"
                        />
                    </div>
                </div>

                <div>
                    <QrParagraph :text="trans('qrcode.text-below')" trans="qrcode.text-below"/>
                    <div class="flex justify-between">
                        <QrInput
                            v-model="qrCode.text_bottom"
                            @changed-debounce="qrCodeStore.update(null, true)"
                            class="w-full"
                        />
                        <StyleSelection
                            :style="qrCode.text_bottom_style"
                            @change="style => qrCode.text_bottom_style = style"
                            class="ml-2"
                        />
                    </div>
                </div>
            </QrForm>
        </div>

        <div class="flex justify-between mt-4">
            <QrButton
                :to="`/create/type/${qrCode.id}`"
                :text="trans('button.back')"
                trans="button.back"
                secondary
                icon="arrow-left"
            />

            <QrButton
                :text="trans('button.continue')"
                trans="button.continue"
                icon-right="arrow-right"
                :disabled="!newQrCode.name"
                :loading="loading"
                :recently-succeeded="recentlySucceeded"
                @click="update"
            />
        </div>
    </div>
</template>

<script setup>
import { trans } from "@/libs/helper.js";
import QrInput from "@/components/application-ui/form/QrInput.vue";
import QrForm from "@/components/application-ui/content/QrForm.vue";
import QrHeader from "@/components/application-ui/content/QrHeader.vue";
import QrButton from "@/components/application-ui/elements/QrButton.vue";
import { useQrCodesStore} from "@/store/qrCodes.js";
import { useStatesStore } from "@/store/states.js";
import { storeToRefs } from "pinia";
import {onMounted, watch} from "vue";
import { useRouter } from "vue-router";
import StyleSelection from "@/components/qr-code/StyleSelection.vue";
import ImageCrop from "@/components/qr-code/ImageCrop.vue";
import QrFilesUpload from "@/components/application-ui/form/QrFilesUpload.vue";
import createUploader from "@/libs/file-uploader.js";
import QrParagraph from "@/components/application-ui/content/QrParagraph.vue";
import Preview from "@/components/qr-code/Preview.vue";

const statesStore = useStatesStore();
const { loading, recentlySucceeded } = storeToRefs(statesStore);
const router = useRouter();
const qrCodeStore = useQrCodesStore();
const { newQrCode, qrCode } = storeToRefs(qrCodeStore);

const update = async () => {
    await qrCodeStore.update();
    await router.push(`/create/order/${qrCode.value.id}`)
};

const uploadComplete = ({ status, data }) => {
    if (status === 200) {
        qrCode.value = data.data
    }
};

const { uploadFiles } = createUploader(`/api/qr-codes/${qrCode.value.id}/image`, uploadComplete);

watch(() => qrCode.value.text_bottom_style, () => qrCodeStore.update(null, true));
watch(() => qrCode.value.text_top_style, () => qrCodeStore.update(null, true));

onMounted(() => {
    newQrCode.value.step = 3

    if (! qrCode.value.text_top_style) {
        qrCode.value.text_top_style = 'default'
    }

    if (! qrCode.value.text_bottom_style) {
        qrCode.value.text_bottom_style = 'default'
    }
});

</script>
